import { useMemo } from 'react'
import { useStore } from '@/stores'
import { useTaxonomyStore } from '@/stores/taxonomyStore'
import {
  isOptionalTaxonomyFallbackEnabled,
  isOtherTaxonomyId,
} from '@/lib/taxonomySelection'

/**
 * Whether the current taxonomy selection may start a NEW capture (recording or
 * note) right now.
 *
 * Beyond requiring asset + indication + KIQ, the selected asset must still be
 * present in the live taxonomy. The recorder filters out assets whose active
 * period has closed, so a stale selection — an asset chosen while open, then its
 * window elapses and the taxonomy refreshes it away — must no longer allow
 * recording or submitting. The server enforces this too (403), but gating the
 * controls stops the user wasting a recording it will reject.
 */
export function useSelectionCaptureReady(): boolean {
  const selectedAssetId = useStore((state) => state.selectedAssetId)
  const selectedIndicationId = useStore((state) => state.selectedIndicationId)
  const selectedKiqIds = useStore((state) => state.selectedKiqIds)
  const assets = useTaxonomyStore((state) => state.data?.assets)

  return useMemo(() => {
    // A real asset must ALWAYS be selected to record — even in the optional-
    // taxonomy fallback build (which otherwise relaxes indication/KIQ). Without
    // an asset there is no active period to honour and the capture would submit
    // the "other" sentinel.
    if (!selectedAssetId || isOtherTaxonomyId(selectedAssetId)) return false

    // The selected asset must still be present in the live taxonomy. Assets
    // whose active period has closed (or that were hidden) are filtered out
    // server-side, so absence means "not recordable right now". Before the
    // taxonomy resolves we can't confirm presence, so don't block first load.
    if (assets && !assets.some((asset) => asset.id === selectedAssetId)) {
      return false
    }

    // Indication + KIQ stay required unless the fallback build relaxes them.
    if (isOptionalTaxonomyFallbackEnabled()) return true
    return Boolean(selectedIndicationId) && selectedKiqIds.length > 0
  }, [assets, selectedAssetId, selectedIndicationId, selectedKiqIds])
}
